/**
 * Developer tools panel — two tabs in one sidebar:
 *
 *  ① Pose Tuning   — hand-author procedural holds and body-state biases.
 *  ② Metadata Edit — browse all animation clips and edit their metadata
 *                    fields (emotion, intensity, tags, etc.). Output is a
 *                    JSON object you paste into anims.metadata.json.
 *
 * ── How Pose Tuning works ─────────────────────────────────────────────────
 * The procedural body system reads `PROCEDURAL_HOLD_POSES[name]` and
 * `BODY_STATE_PROFILES[name].bias` from `body-profiles.ts` every frame.
 * This panel directly mutates those tables; the next frame picks up the
 * change. No special "tuning mode" branch is needed in the runtime.
 *
 * ── How Metadata Edit works ───────────────────────────────────────────────
 * Loads the full animation manifest client-side, lets the user pick a clip,
 * preview it on the figure, and edit all metadata fields in-place. A "Copy
 * as JSON" button formats the edited object — paste it into
 * server/data/anims.metadata.json to save.
 */

import {
  ALL_BODY_STATES,
  ALL_PROCEDURAL_HOLDS,
  BODY_STATE_PROFILES,
  PROCEDURAL_HOLD_POSES,
  type BodyState,
  type BonePose,
  type BoneSlot,
  type EulerOffset,
  type ProceduralHoldName,
} from '../motion/body-profiles';
import type { Figure } from '../motion/figure';
import type { BeatPlayer } from './beat-player';
import { EMOTION_PRESETS } from './beat-player';
import { extractPose, findClips, formatPoseAsCode } from '../dev/extract-pose';
import { smplPoseToBonePose } from '../motion/smpl-retarget';
import { EMOTIONS, GESTURES } from './vocab';

// ── Shared metadata vocabulary (from anims.metadata.json) ─────────────────

const EMOTION_VALUES = [
  'neutral', 'calm', 'joy', 'excitement', 'triumphant',
  'surprise', 'anger', 'tense', 'fear', 'sadness',
  'defeated', 'tired', 'bored', 'confused', 'disgust', 'disbelief',
] as const;

const POSE_VALUES = [
  'standing_neutral', 'standing_arms_raised',
  'seated', 'crouched', 'kneeling',
  'lying_back', 'lying_front', 'unknown',
] as const;

// ── Pose Tuning types ──────────────────────────────────────────────────────

type TuningKind = 'hold' | 'body_state';

interface TuningTarget {
  kind: TuningKind;
  name: ProceduralHoldName | BodyState;
}

interface ClipCandidate {
  name: string;
  defaultTime?: number;
  hint?: string;
  mirror?: 'LR' | 'RL';
}

const CLIP_CANDIDATES: Partial<Record<ProceduralHoldName, ClipCandidate[]>> = {
  hands_clasped_behind_head: [
    { name: 'Legs Crossed, Hands Behind Head',            defaultTime: 1.5, hint: 'seated, both hands behind head' },
    { name: 'Reclined, Hands Behind Head',                defaultTime: 1.0, hint: 'reclined, both hands behind head' },
    { name: 'On Back, Left Leg Bent, Hands Behind Head',  defaultTime: 1.0, hint: 'lying down' },
    { name: 'On Back, Legs Crossed, Hands Behind Head',   defaultTime: 1.0, hint: 'lying down, legs crossed' },
    { name: 'Right Hand Behind Head',                     defaultTime: 1.0, hint: 'right side only — toggle Mirror to fill the left', mirror: 'RL' },
  ],
  hands_on_hips: [
    { name: 'Hands On Hips Looking Over Shoulder',              defaultTime: 0.5, hint: 'direct match' },
    { name: 'Female Sequence - Transition To Hands On Hips',    defaultTime: 2.5, hint: 'settles into pose at end' },
    { name: 'Breakdance Ground Footwork To Hands On Hips Idle', defaultTime: 5.0, hint: 'final idle frame' },
  ],
  hand_on_hip_left: [
    { name: 'Left Hand On Hip',              defaultTime: 1.0, hint: 'direct match' },
    { name: 'Leaning Back, Left Hand On Hip',defaultTime: 1.0, hint: 'with backward lean' },
    { name: 'Legs Crossed, Left Hand On Hip',defaultTime: 1.0, hint: 'seated' },
  ],
  hand_on_hip_right: [
    { name: 'Female Sequence - Transition To Right Hand On Hip', defaultTime: 2.5, hint: 'settles into pose' },
    { name: 'Left Hand On Hip',                                  defaultTime: 1.0, hint: 'mirror of left version', mirror: 'LR' },
  ],
  hand_in_pocket_right: [
    { name: 'Searching Front And Back Pockets', defaultTime: 1.5, hint: 'mid-search frame' },
  ],
  hands_clasped_front:       [],
  hands_clasped_behind_back: [],
};

function candidatesFor(target: TuningTarget): ClipCandidate[] {
  if (target.kind !== 'hold') return [];
  return CLIP_CANDIDATES[target.name as ProceduralHoldName] ?? [];
}

const AXES = ['x', 'y', 'z'] as const;
type Axis = (typeof AXES)[number];

const BONE_GROUPS: { label: string; slots: BoneSlot[] }[] = [
  { label: 'Spine + Neck',  slots: ['hips', 'spine', 'spine1', 'spine2', 'neck'] },
  { label: 'Right arm',     slots: ['clavicleR', 'upperArmR', 'forearmR', 'handR'] },
  { label: 'Left arm',      slots: ['clavicleL', 'upperArmL', 'forearmL', 'handL'] },
  { label: 'Right leg',     slots: ['upLegR', 'legR', 'footR'] },
  { label: 'Left leg',      slots: ['upLegL', 'legL', 'footL'] },
];

type PoseSnapshot = Map<string, BonePose>;

function deepClonePose(p: BonePose): BonePose {
  const out: BonePose = {};
  for (const slot of Object.keys(p) as BoneSlot[]) {
    const o = p[slot];
    if (!o) continue;
    out[slot] = { ...o };
  }
  return out;
}

function getPose(target: TuningTarget): BonePose {
  if (target.kind === 'hold') return PROCEDURAL_HOLD_POSES[target.name as ProceduralHoldName];
  return BODY_STATE_PROFILES[target.name as BodyState].bias;
}

function radToDeg(rad: number): number {
  return (rad * 180) / Math.PI;
}

// ── Metadata edit types ────────────────────────────────────────────────────

interface MetaEntry {
  name: string;
  url: string;
  emotion: string;
  intensity: number;
  is_idle: boolean;
  is_transition: boolean;
  loopable: boolean;
  symmetric: boolean;
  tags: string[];
  start_pose: string;
  end_pose: string;
}

// ── Public API ─────────────────────────────────────────────────────────────

export interface SplatTransform {
  scale: number;
  posX:  number;
  posY:  number;
  posZ:  number;
  rotY:  number; // degrees
  clipW: number; // sphere exclusion radius around camera in view-space units, 0 = disabled
  clipD: number; // reserved — not currently used by the shader
}

export const SPLAT_TRANSFORM_DEFAULTS: SplatTransform = {
  scale: 10,
  posX:  0,
  posY:  -2,
  posZ:  0,
  rotY:  0,
  clipW: 0,
  clipD: 0,
};

export const SPLAT_TRANSFORM_LS_KEY = 'body-field:splat-transform';

export interface TuningPanel {
  open: (initialTab?: 'pose' | 'meta' | 'emotions' | 'world') => void;
  close: () => void;
  readonly isOpen: boolean;
  toggle: () => void;
  destroy: () => void;
}

export interface TuningPanelOpts {
  figure: Figure;
  beatPlayer: BeatPlayer;
  container?: HTMLElement;
  idleClipUrl?: string;
  serverUrl?: string;
  /** Called whenever the splat transform sliders change. */
  onSplatTransformChange?: (t: SplatTransform) => void;
}

// ── createTuningPanel ──────────────────────────────────────────────────────

export function createTuningPanel(opts: TuningPanelOpts): TuningPanel {
  const { figure, beatPlayer } = opts;
  const idleClipUrl = opts.idleClipUrl;
  const serverUrl = opts.serverUrl;
  const container = opts.container ?? document.body;
  let isOpen = false;

  // ── Root DOM ─────────────────────────────────────────────────────────────
  const root = document.createElement('div');
  root.id = 'tuning-panel';
  root.className = 'tuning-panel hidden';
  root.innerHTML = `
    <div class="tuning-header">
      <div class="tuning-tabs">
        <button class="tuning-tab-btn tuning-tab-pose active" type="button">Pose Tuning</button>
        <button class="tuning-tab-btn tuning-tab-meta" type="button">Metadata Edit</button>
        <button class="tuning-tab-btn tuning-tab-emotions" type="button">Emotions</button>
        <button class="tuning-tab-btn tuning-tab-world" type="button">World</button>
      </div>
      <button class="tuning-close" type="button" aria-label="Close">×</button>
    </div>

    <!-- ── POSE TUNING PANE ───────────────────────────────────────────── -->
    <div class="tab-pane tab-pane-pose">
      <div class="tuning-row">
        <label class="tuning-label">Target</label>
        <select class="tuning-target"></select>
      </div>

      <div class="tuning-row tuning-clip-row hidden">
        <label class="tuning-label">From clip</label>
        <select class="tuning-clip"></select>
      </div>
      <div class="tuning-row tuning-allclips-search-row hidden">
        <label class="tuning-label">All clips</label>
        <input class="tuning-allclips-search" type="search" placeholder="search…" autocomplete="off" />
        <span class="tuning-allclips-count"></span>
      </div>
      <div class="tuning-row tuning-allclips-list-row hidden">
        <label class="tuning-label"></label>
        <select class="tuning-allclips" size="6"></select>
      </div>
      <div class="tuning-row tuning-clip-time-row hidden">
        <label class="tuning-label">Time</label>
        <input class="tuning-clip-time" type="range" min="0" max="10" step="0.05" value="1.0" />
        <span class="tuning-clip-time-value">1.00 s</span>
        <label class="tuning-clip-mirror">
          <input class="tuning-clip-mirror-cb" type="checkbox" /> Mirror
        </label>
      </div>
      <div class="tuning-row tuning-allclips-actions-row hidden">
        <label class="tuning-label"></label>
        <button class="tuning-allclips-extract" type="button" disabled>Copy pose at this time</button>
        <span class="tuning-allclips-actions-hint">Copies BonePose code to clipboard — paste into body-profiles.ts</span>
      </div>

      <div class="tuning-row tuning-posescript-search-row hidden">
        <label class="tuning-label">PoseScript</label>
        <input class="tuning-posescript-search" type="search"
               placeholder="describe a pose, e.g. 'arms crossed'" autocomplete="off" />
        <span class="tuning-posescript-stats"></span>
      </div>
      <div class="tuning-row tuning-posescript-results-row hidden">
        <label class="tuning-label"></label>
        <select class="tuning-posescript-results" size="6"></select>
      </div>

      <div class="tuning-actions">
        <button class="tuning-mirror" type="button">Mirror L → R</button>
        <button class="tuning-mirror-rl" type="button">Mirror R → L</button>
        <button class="tuning-zero" type="button">Zero all</button>
        <button class="tuning-reset" type="button">Reset to original</button>
        <button class="tuning-copy" type="button">Copy as code</button>
      </div>

      <div class="tuning-sliders"></div>

      <div class="tuning-status tuning-pose-status" aria-live="polite"></div>
    </div>

    <!-- ── METADATA EDIT PANE ─────────────────────────────────────────── -->
    <div class="tab-pane tab-pane-meta hidden">

      <div class="meta-section-title">Select Clip</div>

      <div class="tuning-row">
        <label class="tuning-label">Search</label>
        <input class="meta-clip-search" type="search" placeholder="search animations…" autocomplete="off" />
        <span class="meta-clip-count"></span>
      </div>
      <div class="tuning-row">
        <label class="tuning-label"></label>
        <select class="meta-clip-list" size="6"></select>
      </div>
      <div class="tuning-row">
        <label class="tuning-label"></label>
        <button class="meta-preview-btn" type="button" disabled>▶ Preview</button>
        <span class="meta-preview-hint">Plays the selected clip on the figure</span>
      </div>

      <div class="meta-fields hidden">

        <div class="meta-section-title">Edit Metadata</div>

        <!-- emotion -->
        <div class="meta-field-row">
          <label class="meta-field-label">Emotion</label>
          <span class="meta-field-desc">The overall emotional tone of this clip. The LLM matches animations to the scene's emotional state using this value.</span>
          <select class="meta-emotion"></select>
        </div>

        <!-- intensity -->
        <div class="meta-field-row">
          <label class="meta-field-label">Intensity</label>
          <span class="meta-field-desc">How emotionally loaded is this clip? 0 = flat / barely noticeable, 1 = peak / extreme. The LLM uses this to match the scene's emotional strength.</span>
          <div class="meta-slider-row">
            <input type="range" class="meta-intensity" min="0" max="1" step="0.05" />
            <span class="meta-num-val meta-intensity-val">0.50</span>
          </div>
        </div>

        <!-- booleans -->
        <div class="meta-field-row">
          <label class="meta-field-label">Flags</label>
          <span class="meta-field-desc">Structural properties of the clip used for scheduling and catalog filtering.</span>
          <div class="meta-booleans">
            <label class="meta-bool-item">
              <input type="checkbox" class="meta-is-idle" />
              <span class="meta-bool-name">is_idle</span>
              <span class="meta-bool-desc">Suitable as a looping background idle — character is essentially at rest.</span>
            </label>
            <label class="meta-bool-item">
              <input type="checkbox" class="meta-loopable" />
              <span class="meta-bool-name">loopable</span>
              <span class="meta-bool-desc">End pose matches start pose well enough to loop without a visible seam.</span>
            </label>
            <label class="meta-bool-item">
              <input type="checkbox" class="meta-is-transition" />
              <span class="meta-bool-name">is_transition</span>
              <span class="meta-bool-desc">One-shot transition between two body poses — not meant to loop.</span>
            </label>
            <label class="meta-bool-item">
              <input type="checkbox" class="meta-symmetric" />
              <span class="meta-bool-name">symmetric</span>
              <span class="meta-bool-desc">Looks identical when mirrored left↔right — the player randomly flips it for variety.</span>
            </label>
          </div>
        </div>

        <!-- tags -->
        <div class="meta-field-row">
          <label class="meta-field-label">Tags</label>
          <span class="meta-field-desc">Keywords the LLM and catalog deny-filters use to find or block this clip. Press Enter or comma to add; click × on a chip to remove.</span>
          <div class="meta-tags-editor">
            <div class="meta-tags-chips"></div>
            <input type="text" class="meta-tags-input" placeholder="type a tag, press Enter…" autocomplete="off" />
          </div>
        </div>

        <!-- start_pose -->
        <div class="meta-field-row">
          <label class="meta-field-label">Start pose</label>
          <span class="meta-field-desc">The body position the character must be in before this clip can play. Used to sequence clips that require a specific entry pose (e.g. cannot walk into a seated clip).</span>
          <select class="meta-start-pose"></select>
        </div>

        <!-- end_pose -->
        <div class="meta-field-row">
          <label class="meta-field-label">End pose</label>
          <span class="meta-field-desc">The body position the character is in after this clip finishes. The next clip's start_pose should match this — used to chain animations smoothly.</span>
          <select class="meta-end-pose"></select>
        </div>

        <div class="meta-actions">
          <button class="meta-save-btn" type="button">Save to catalog</button>
          <span class="meta-actions-hint">Writes changes directly to anims.metadata.json</span>
        </div>

      </div>

      <div class="tuning-status meta-status" aria-live="polite"></div>
    </div>

    <!-- ── EMOTIONS PANE ─────────────────────────────────────────────── -->
    <div class="tab-pane tab-pane-emotions hidden">

      <div class="emo-section">
        <div class="meta-section-title">Emotions</div>
        <p class="emo-hint">Click to preview a facial emotion preset on the character. Adjust intensity with the slider.</p>
        <div class="emo-intensity-row">
          <label class="emo-intensity-label">Intensity</label>
          <input type="range" class="emo-intensity-slider" min="0" max="1" step="0.05" value="1" />
          <span class="emo-intensity-val">1.00</span>
        </div>
        <div class="emo-pills emo-emotion-pills"></div>
      </div>

      <div class="emo-section">
        <div class="meta-section-title">Gestures</div>
        <p class="emo-hint">One-shot facial gesture animations — jaw, brows, head movement. Plays once and auto-clears.</p>
        <div class="emo-pills emo-gesture-pills"></div>
      </div>

      <div class="emo-section">
        <button class="emo-clear-btn" type="button">Clear / Reset to neutral</button>
      </div>

      <div class="tuning-status emo-status" aria-live="polite"></div>
    </div>

    <!-- ── WORLD PANE ─────────────────────────────────────────────────── -->
    <div class="tab-pane tab-pane-world hidden">
      <div class="meta-section-title">Splat Transform</div>
      <p class="emo-hint" style="padding:0 12px 6px">Adjust the active environment splat in real-time. Values are saved to localStorage.</p>

      <div class="world-slider-row">
        <label class="world-slider-label">Scale</label>
        <input type="range" class="world-scale" min="0.5" max="50" step="0.5" value="10" />
        <span class="world-slider-val world-scale-val">10.0</span>
      </div>
      <div class="world-slider-row">
        <label class="world-slider-label">Pos X</label>
        <input type="range" class="world-pos-x" min="-20" max="20" step="0.1" value="0" />
        <span class="world-slider-val world-pos-x-val">0.0</span>
      </div>
      <div class="world-slider-row">
        <label class="world-slider-label">Pos Y</label>
        <input type="range" class="world-pos-y" min="-20" max="20" step="0.1" value="-2" />
        <span class="world-slider-val world-pos-y-val">-2.0</span>
      </div>
      <div class="world-slider-row">
        <label class="world-slider-label">Pos Z</label>
        <input type="range" class="world-pos-z" min="-20" max="20" step="0.1" value="0" />
        <span class="world-slider-val world-pos-z-val">0.0</span>
      </div>
      <div class="world-slider-row">
        <label class="world-slider-label">Rot Y</label>
        <input type="range" class="world-rot-y" min="-180" max="180" step="1" value="0" />
        <span class="world-slider-val world-rot-y-val">0°</span>
      </div>
      <div class="world-slider-row">
        <label class="world-slider-label">Near R</label>
        <input type="range" class="world-clip-w" min="0" max="30" step="0.5" value="0" />
        <span class="world-slider-val world-clip-w-val">off</span>
      </div>
      <div class="world-slider-row">
        <label class="world-slider-label">Clip D</label>
        <input type="range" class="world-clip-d" min="0" max="30" step="0.5" value="0" />
        <span class="world-slider-val world-clip-d-val">off</span>
      </div>

      <div class="meta-actions" style="padding:8px 12px">
        <button class="world-reset-btn" type="button">Reset to defaults</button>
      </div>
      <div class="tuning-status world-status" aria-live="polite"></div>
    </div>
  `;
  container.appendChild(root);

  // ── Tab switching ─────────────────────────────────────────────────────────
  const tabPoseBtn  = root.querySelector('.tuning-tab-pose') as HTMLButtonElement;
  const tabMetaBtn  = root.querySelector('.tuning-tab-meta') as HTMLButtonElement;
  const tabEmoBtn   = root.querySelector('.tuning-tab-emotions') as HTMLButtonElement;
  const tabWorldBtn = root.querySelector('.tuning-tab-world') as HTMLButtonElement;
  const posePane    = root.querySelector('.tab-pane-pose') as HTMLDivElement;
  const metaPane    = root.querySelector('.tab-pane-meta') as HTMLDivElement;
  const emoPane     = root.querySelector('.tab-pane-emotions') as HTMLDivElement;
  const worldPane   = root.querySelector('.tab-pane-world') as HTMLDivElement;
  const closeBtn    = root.querySelector('.tuning-close') as HTMLButtonElement;

  function showTab(tab: 'pose' | 'meta' | 'emotions' | 'world'): void {
    posePane.classList.toggle('hidden', tab !== 'pose');
    metaPane.classList.toggle('hidden', tab !== 'meta');
    emoPane.classList.toggle('hidden',  tab !== 'emotions');
    worldPane.classList.toggle('hidden', tab !== 'world');
    tabPoseBtn.classList.toggle('active',  tab === 'pose');
    tabMetaBtn.classList.toggle('active',  tab === 'meta');
    tabEmoBtn.classList.toggle('active',   tab === 'emotions');
    tabWorldBtn.classList.toggle('active', tab === 'world');

    if (tab === 'pose') {
      if (currentTarget) applyTarget(currentTarget);
    } else if (tab === 'meta') {
      figure.releaseAllHolds(0.2);
      figure.setBodyState('neutral_idle');
      if (clipMode === 'preview') revertToIdle();
      void ensureAllClipsLoaded().then(() => renderMetaClips(metaClipSearch.value));
    } else if (tab === 'emotions' || tab === 'world') {
      figure.releaseAllHolds(0.2);
      figure.setBodyState('neutral_idle');
      if (clipMode === 'preview') revertToIdle();
    }
  }

  tabPoseBtn.addEventListener('click',  () => showTab('pose'));
  tabMetaBtn.addEventListener('click',  () => showTab('meta'));
  tabEmoBtn.addEventListener('click',   () => showTab('emotions'));
  tabWorldBtn.addEventListener('click', () => showTab('world'));

  let currentTarget: TuningTarget | null = null;
  const snapshots: PoseSnapshot = new Map();

  const targetSelect  = root.querySelector('.tuning-target') as HTMLSelectElement;
  const slidersHost   = root.querySelector('.tuning-sliders') as HTMLDivElement;
  const poseStatusEl  = root.querySelector('.tuning-pose-status') as HTMLDivElement;
  const mirrorLRBtn   = root.querySelector('.tuning-mirror') as HTMLButtonElement;
  const mirrorRLBtn   = root.querySelector('.tuning-mirror-rl') as HTMLButtonElement;
  const zeroBtn       = root.querySelector('.tuning-zero') as HTMLButtonElement;
  const resetBtn      = root.querySelector('.tuning-reset') as HTMLButtonElement;
  const copyBtn       = root.querySelector('.tuning-copy') as HTMLButtonElement;
  const clipRow       = root.querySelector('.tuning-clip-row') as HTMLDivElement;
  const clipTimeRow   = root.querySelector('.tuning-clip-time-row') as HTMLDivElement;
  const clipSelect    = root.querySelector('.tuning-clip') as HTMLSelectElement;
  const clipTimeInput = root.querySelector('.tuning-clip-time') as HTMLInputElement;
  const clipTimeValue = root.querySelector('.tuning-clip-time-value') as HTMLSpanElement;
  const clipMirrorCb  = root.querySelector('.tuning-clip-mirror-cb') as HTMLInputElement;
  const allClipsSearchRow   = root.querySelector('.tuning-allclips-search-row') as HTMLDivElement;
  const allClipsListRow     = root.querySelector('.tuning-allclips-list-row') as HTMLDivElement;
  const allClipsActionsRow  = root.querySelector('.tuning-allclips-actions-row') as HTMLDivElement;
  const allClipsSearch      = root.querySelector('.tuning-allclips-search') as HTMLInputElement;
  const allClipsSelect      = root.querySelector('.tuning-allclips') as HTMLSelectElement;
  const allClipsCount       = root.querySelector('.tuning-allclips-count') as HTMLSpanElement;
  const allClipsExtractBtn  = root.querySelector('.tuning-allclips-extract') as HTMLButtonElement;
  const poseSearchRow       = root.querySelector('.tuning-posescript-search-row') as HTMLDivElement;
  const poseResultsRow      = root.querySelector('.tuning-posescript-results-row') as HTMLDivElement;
  const poseSearchInput     = root.querySelector('.tuning-posescript-search') as HTMLInputElement;
  const poseResultsSelect   = root.querySelector('.tuning-posescript-results') as HTMLSelectElement;
  const poseStatsSpan       = root.querySelector('.tuning-posescript-stats') as HTMLSpanElement;

  // Populate target dropdown
  {
    const holdGroup = document.createElement('optgroup');
    holdGroup.label = 'Procedural holds';
    for (const name of ALL_PROCEDURAL_HOLDS) {
      const opt = document.createElement('option');
      opt.value = `hold:${name}`;
      opt.textContent = name;
      holdGroup.appendChild(opt);
    }
    targetSelect.appendChild(holdGroup);

    const stateGroup = document.createElement('optgroup');
    stateGroup.label = 'Body states (bias)';
    for (const name of ALL_BODY_STATES) {
      const opt = document.createElement('option');
      opt.value = `body_state:${name}`;
      opt.textContent = name;
      stateGroup.appendChild(opt);
    }
    targetSelect.appendChild(stateGroup);
  }

  // ── Sliders ───────────────────────────────────────────────────────────────
  interface SliderRef { slot: BoneSlot; axis: Axis; input: HTMLInputElement; valueEl: HTMLSpanElement; }
  let sliderRefs: SliderRef[] = [];

  function buildSliders(): void {
    sliderRefs = [];
    slidersHost.innerHTML = '';
    if (!currentTarget) return;
    const pose = getPose(currentTarget);
    for (const group of BONE_GROUPS) {
      const groupEl = document.createElement('div');
      groupEl.className = 'tuning-group';
      const titleEl = document.createElement('div');
      titleEl.className = 'tuning-group-title';
      titleEl.textContent = group.label;
      groupEl.appendChild(titleEl);
      for (const slot of group.slots) {
        const rowEl = document.createElement('div');
        rowEl.className = 'tuning-bone';
        const nameEl = document.createElement('div');
        nameEl.className = 'tuning-bone-name';
        nameEl.textContent = slot;
        rowEl.appendChild(nameEl);
        const axesEl = document.createElement('div');
        axesEl.className = 'tuning-axes';
        for (const axis of AXES) {
          const initial = pose[slot]?.[axis] ?? 0;
          const axisEl = document.createElement('div');
          axisEl.className = `tuning-axis tuning-axis-${axis}`;
          const labelEl = document.createElement('span');
          labelEl.className = 'tuning-axis-label';
          labelEl.textContent = axis;
          const input = document.createElement('input');
          input.type = 'range';
          input.min = '-2.0';
          input.max = '2.0';
          input.step = '0.01';
          input.value = String(initial);
          const valueEl = document.createElement('span');
          valueEl.className = 'tuning-axis-value';
          valueEl.textContent = `${initial.toFixed(2)} (${radToDeg(initial).toFixed(0)}°)`;
          axisEl.append(labelEl, input, valueEl);
          axesEl.appendChild(axisEl);
          const ref: SliderRef = { slot, axis, input, valueEl };
          sliderRefs.push(ref);
          input.addEventListener('input', () => {
            const v = parseFloat(input.value);
            writePose(slot, axis, v);
            valueEl.textContent = `${v.toFixed(2)} (${radToDeg(v).toFixed(0)}°)`;
          });
        }
        rowEl.appendChild(axesEl);
        groupEl.appendChild(rowEl);
      }
      slidersHost.appendChild(groupEl);
    }
  }

  function writePose(slot: BoneSlot, axis: Axis, value: number): void {
    if (!currentTarget) return;
    const pose = getPose(currentTarget);
    let entry: EulerOffset | undefined = pose[slot];
    if (!entry) { entry = {}; pose[slot] = entry; }
    entry[axis] = value;
  }

  function refreshSlidersFromPose(): void {
    if (!currentTarget) return;
    const pose = getPose(currentTarget);
    for (const ref of sliderRefs) {
      const v = pose[ref.slot]?.[ref.axis] ?? 0;
      ref.input.value = String(v);
      ref.valueEl.textContent = `${v.toFixed(2)} (${radToDeg(v).toFixed(0)}°)`;
    }
  }

  // ── Target switching ───────────────────────────────────────────────────────
  function applyTarget(target: TuningTarget): void {
    currentTarget = target;
    figure.releaseAllHolds(0.1);
    figure.setBodyState('neutral_idle');
    if (target.kind === 'hold') void figure.setHold(target.name, '', 1.0);
    else figure.setBodyState(target.name);
    buildSliders();
    populateClipDropdown(target);
    poseStatusEl.textContent = `Tuning ${target.kind}: ${target.name}`;
  }

  // ── Clip extract ───────────────────────────────────────────────────────────
  type ClipMode = 'idle' | 'extract' | 'preview';
  let clipMode: ClipMode = 'idle';
  let activeClipName: string | null = null;
  let activeMirrorDirection: 'LR' | 'RL' = 'LR';
  let clipBusy = false;
  let pendingExtract = false;

  function syncClipControlVisibility(): void {
    if (clipMode === 'extract' || (clipMode === 'preview' && activeClipName)) {
      clipTimeRow.classList.remove('hidden');
    } else {
      clipTimeRow.classList.add('hidden');
    }
  }

  function revertToIdle(): void {
    if (idleClipUrl) figure.playClipFromUrl(idleClipUrl, 0.2).catch(() => {/* silent */});
  }

  function populateClipDropdown(target: TuningTarget): void {
    const candidates = candidatesFor(target);
    clipSelect.innerHTML = '';
    allClipsSearchRow.classList.remove('hidden');
    allClipsListRow.classList.remove('hidden');
    allClipsActionsRow.classList.remove('hidden');
    if (target.kind !== 'hold' || candidates.length === 0) {
      clipRow.classList.add('hidden');
    } else {
      clipRow.classList.remove('hidden');
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = '— pick a curated clip —';
      clipSelect.appendChild(placeholder);
      for (let i = 0; i < candidates.length; i += 1) {
        const c = candidates[i];
        const opt = document.createElement('option');
        opt.value = String(i);
        opt.textContent = c.hint ? `${c.name}  (${c.hint})` : c.name;
        clipSelect.appendChild(opt);
      }
      clipSelect.value = '';
    }
    clipMode = 'idle';
    activeClipName = null;
    activeMirrorDirection = 'LR';
    clipMirrorCb.checked = false;
    allClipsExtractBtn.disabled = true;
    syncClipControlVisibility();
  }

  async function runExtraction(): Promise<void> {
    if (!currentTarget || currentTarget.kind !== 'hold') return;
    if (clipMode !== 'extract' || !activeClipName) return;
    if (clipBusy) { pendingExtract = true; return; }
    const targetAtStart = currentTarget;
    const clipNameAtStart = activeClipName;
    const directionAtStart: 'LR' | 'RL' | undefined = clipMirrorCb.checked ? activeMirrorDirection : undefined;
    const tAtStart = parseFloat(clipTimeInput.value) || 0;
    clipBusy = true;
    poseStatusEl.textContent = `Extracting from "${clipNameAtStart}"…`;
    try {
      const result = await extractPose({ clip: clipNameAtStart, time: tAtStart, mirror: directionAtStart, log: false });
      if (currentTarget !== targetAtStart || activeClipName !== clipNameAtStart) return;
      const pose = getPose(targetAtStart);
      for (const slot of Object.keys(pose) as BoneSlot[]) delete pose[slot];
      for (const slot of Object.keys(result.pose) as BoneSlot[]) {
        const o = result.pose[slot];
        if (o) pose[slot] = { ...o };
      }
      refreshSlidersFromPose();
      if (Number.isFinite(result.duration) && result.duration > 0) {
        clipTimeInput.max = result.duration.toFixed(2);
      }
      poseStatusEl.textContent = `Loaded "${clipNameAtStart}" @ ${tAtStart.toFixed(2)}s${directionAtStart ? ` (mirror ${directionAtStart})` : ''}`;
    } catch (err) {
      poseStatusEl.textContent = `Extraction failed: ${err instanceof Error ? err.message : String(err)}`;
    } finally {
      clipBusy = false;
      if (pendingExtract) { pendingExtract = false; void runExtraction(); }
    }
  }

  clipSelect.addEventListener('change', () => {
    if (!currentTarget || currentTarget.kind !== 'hold') return;
    const candidates = candidatesFor(currentTarget);
    const idx = parseInt(clipSelect.value, 10);
    if (!Number.isFinite(idx) || idx < 0 || idx >= candidates.length) return;
    const c = candidates[idx];
    if (clipMode === 'preview') {
      revertToIdle();
      const pose = getPose(currentTarget);
      for (const slot of Object.keys(pose) as BoneSlot[]) delete pose[slot];
      refreshSlidersFromPose();
      const holdName = currentTarget.name as string;
      figure.releaseHold(holdName, 0.01);
      setTimeout(() => { if (clipMode === 'extract') void figure.setHold(holdName, '', 1.0); }, 300);
    }
    clipMode = 'extract';
    activeClipName = c.name;
    activeMirrorDirection = c.mirror ?? 'LR';
    clipMirrorCb.checked = c.mirror != null;
    if (c.defaultTime != null) {
      clipTimeInput.value = String(c.defaultTime);
      clipTimeValue.textContent = `${c.defaultTime.toFixed(2)} s`;
    }
    allClipsSelect.value = '';
    syncClipControlVisibility();
    void runExtraction();
  });

  // Shared all-clips store (used by both pose tuning and metadata edit)
  let allClipNames: string[] = [];
  const allClipsByName = new Map<string, string>();
  /** Full metadata records from /catalog, indexed by clip name. */
  const allClipsMeta = new Map<string, Record<string, unknown>>();

  async function ensureAllClipsLoaded(): Promise<void> {
    if (allClipNames.length > 0) return;
    try {
      // Use /catalog (served by the director server) so we get the full
      // metadata object — emotion, intensity, tags, loopable, etc. — not
      // just the name+url that anims.json carries.
      let entries: Array<Record<string, unknown>> = [];
      if (serverUrl) {
        const res = await fetch(`${serverUrl}/catalog?all=true`);
        if (res.ok) {
          const data = (await res.json()) as { clips: Array<Record<string, unknown>> };
          entries = data.clips;
        }
      }
      // Fallback: load bare name+url from anims.json when the server is
      // not available (pose-tuning still works; metadata fields will be empty).
      if (entries.length === 0) {
        const fallback = await findClips('');
        entries = fallback as unknown as Array<Record<string, unknown>>;
      }
      const sorted = [...entries].sort((a, b) =>
        String(a['name']).localeCompare(String(b['name']))
      );
      allClipNames = sorted.map((c) => String(c['name']));
      for (const c of sorted) {
        const name = String(c['name']);
        allClipsByName.set(name, String(c['url'] ?? ''));
        allClipsMeta.set(name, c);
      }
    } catch (err) {
      console.warn('[tuning] failed to load clips manifest', err);
    }
  }

  // Pose-tuning all-clips list
  function renderAllClips(filter: string): void {
    const trimmed = filter.trim().toLowerCase();
    const tokens = trimmed.length > 0 ? trimmed.split(/\s+/) : [];
    const matches: string[] = [];
    for (const name of allClipNames) {
      if (tokens.length === 0 || tokens.every((t) => name.toLowerCase().includes(t))) matches.push(name);
    }
    allClipsSelect.innerHTML = '';
    for (const name of matches.slice(0, 2500)) {
      const opt = document.createElement('option');
      opt.value = name;
      opt.textContent = name;
      allClipsSelect.appendChild(opt);
    }
    allClipsCount.textContent = matches.length === allClipNames.length
      ? `${allClipNames.length}`
      : `${matches.length} / ${allClipNames.length}`;
  }

  allClipsSearch.addEventListener('input', () => renderAllClips(allClipsSearch.value));

  allClipsSelect.addEventListener('change', () => {
    const name = allClipsSelect.value;
    if (!name) { allClipsExtractBtn.disabled = true; clipTimeRow.classList.add('hidden'); return; }
    const url = allClipsByName.get(name);
    if (!url) { poseStatusEl.textContent = `No URL for clip "${name}"`; allClipsExtractBtn.disabled = true; return; }
    if (currentTarget?.kind === 'hold') figure.releaseHold(currentTarget.name as string, 0.15);
    clipMode = 'preview';
    activeClipName = name;
    clipSelect.value = '';
    clipTimeRow.classList.remove('hidden');
    allClipsExtractBtn.disabled = false;
    poseStatusEl.textContent = `Previewing "${name}" — scrub Time, then "Copy pose at this time"`;
    figure.playClipFromUrl(url, 0.25, true).catch((err) => {
      poseStatusEl.textContent = `Preview failed: ${err instanceof Error ? err.message : String(err)}`;
    });
  });

  allClipsExtractBtn.addEventListener('click', () => {
    const clipName = allClipsSelect.value;
    if (!clipName) { poseStatusEl.textContent = 'Pick a clip from the list first.'; return; }
    const t = parseFloat(clipTimeInput.value) || 0;
    const mirror = clipMirrorCb.checked ? activeMirrorDirection : undefined;
    poseStatusEl.textContent = `Extracting "${clipName}" @ ${t.toFixed(2)}s…`;
    extractPose({ clip: clipName, time: t, mirror, log: false })
      .then(({ pose }) => {
        const code = formatPoseAsCode(pose);
        void navigator.clipboard.writeText(code).then(() => {
          poseStatusEl.textContent = `✓ Copied! Paste into body-profiles.ts → PROCEDURAL_HOLD_POSES['your_hold_name']`;
        }).catch(() => {
          poseStatusEl.textContent = `Copy failed — pose: ${code}`;
        });
      })
      .catch((err) => {
        poseStatusEl.textContent = `Extract failed: ${err instanceof Error ? err.message : String(err)}`;
      });
  });

  clipTimeInput.addEventListener('input', () => {
    const t = parseFloat(clipTimeInput.value) || 0;
    clipTimeValue.textContent = `${t.toFixed(2)} s`;
    if (clipMode === 'extract') void runExtraction();
  });

  clipMirrorCb.addEventListener('change', () => {
    if (clipMode === 'extract') void runExtraction();
  });

  // ── PoseScript ─────────────────────────────────────────────────────────────
  const poseScriptPoses = new Map<string, number[]>();
  let poseSearchSeq = 0;
  let poseSearchTimer: ReturnType<typeof setTimeout> | null = null;

  function setPoseSectionVisible(visible: boolean): void {
    poseSearchRow.classList.toggle('hidden', !visible);
    poseResultsRow.classList.toggle('hidden', !visible);
  }

  async function fetchPoseStats(): Promise<void> {
    if (!serverUrl) return;
    try {
      const res = await fetch(`${serverUrl}/pose-search/stats`);
      if (!res.ok) return;
      const data = (await res.json()) as { total: number; seed: number; preprocessed: number };
      const labels: string[] = [];
      if (data.seed > 0) labels.push(`${data.seed} seed`);
      if (data.preprocessed > 0) labels.push(`${data.preprocessed} PoseScript`);
      poseStatsSpan.textContent = labels.length > 0 ? `(${labels.join(' + ')} loaded)` : '(empty)';
    } catch (_) {
      poseStatsSpan.textContent = '(stats unavailable)';
    }
  }

  async function runPoseSearch(query: string): Promise<void> {
    if (!serverUrl) return;
    poseSearchSeq += 1;
    const seq = poseSearchSeq;
    try {
      const res = await fetch(`${serverUrl}/pose-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, topN: 12 }),
      });
      if (!res.ok) {
        if (seq === poseSearchSeq) { poseResultsSelect.innerHTML = ''; poseStatusEl.textContent = `PoseScript search failed: HTTP ${res.status}`; }
        return;
      }
      const data = (await res.json()) as { candidates: Array<{ id: string; caption: string; smpl_pose: number[]; source: string }>; total: number };
      if (seq !== poseSearchSeq) return;
      poseScriptPoses.clear();
      poseResultsSelect.innerHTML = '';
      if (data.candidates.length === 0) {
        const opt = document.createElement('option');
        opt.disabled = true;
        opt.textContent = `(no matches in ${data.total} pose(s))`;
        poseResultsSelect.appendChild(opt);
        return;
      }
      for (const c of data.candidates) {
        poseScriptPoses.set(c.id, c.smpl_pose);
        const opt = document.createElement('option');
        opt.value = c.id;
        opt.textContent = `${c.source === 'seed' ? '◦ seed' : '• PoseScript'}  ${c.caption}`;
        opt.title = c.caption;
        poseResultsSelect.appendChild(opt);
      }
    } catch (err) {
      if (seq !== poseSearchSeq) return;
      poseResultsSelect.innerHTML = '';
      poseStatusEl.textContent = `PoseScript search failed: ${err instanceof Error ? err.message : String(err)}`;
    }
  }

  function applyPoseScriptResult(id: string): void {
    if (!currentTarget || currentTarget.kind !== 'hold') { poseStatusEl.textContent = 'PoseScript: pick a hold target first'; return; }
    const smpl = poseScriptPoses.get(id);
    if (!smpl) { poseStatusEl.textContent = `PoseScript: pose "${id}" no longer cached — search again`; return; }
    let retargeted: BonePose;
    try {
      retargeted = smplPoseToBonePose(smpl, figure.getBindRotations(), { debug: true, label: id });
    } catch (err) {
      poseStatusEl.textContent = `PoseScript retarget failed: ${err instanceof Error ? err.message : String(err)}`;
      return;
    }
    if (clipMode === 'preview') revertToIdle();
    clipMode = 'idle';
    activeClipName = null;
    clipSelect.value = '';
    allClipsSelect.value = '';
    syncClipControlVisibility();
    const pose = getPose(currentTarget);
    for (const slot of Object.keys(pose) as BoneSlot[]) delete pose[slot];
    for (const slot of Object.keys(retargeted) as BoneSlot[]) {
      const o = retargeted[slot];
      if (o) pose[slot] = { ...o };
    }
    refreshSlidersFromPose();
    poseStatusEl.textContent = `PoseScript: applied "${id}" (refine with sliders, then Copy as code)`;
  }

  poseSearchInput.addEventListener('input', () => {
    if (poseSearchTimer) clearTimeout(poseSearchTimer);
    poseSearchTimer = setTimeout(() => void runPoseSearch(poseSearchInput.value), 220);
  });
  poseResultsSelect.addEventListener('change', () => { const id = poseResultsSelect.value; if (id) applyPoseScriptResult(id); });
  targetSelect.addEventListener('change', () => {
    const v = targetSelect.value;
    const sep = v.indexOf(':');
    if (sep < 0) return;
    applyTarget({ kind: v.slice(0, sep) as TuningKind, name: v.slice(sep + 1) } as TuningTarget);
  });

  // ── Mirror / zero / reset ──────────────────────────────────────────────────
  const MIRROR_PAIRS: Array<[BoneSlot, BoneSlot]> = [
    ['clavicleL', 'clavicleR'], ['upperArmL', 'upperArmR'], ['forearmL', 'forearmR'], ['handL', 'handR'],
    ['upLegL', 'upLegR'], ['legL', 'legR'], ['footL', 'footR'],
  ];

  function mirror(direction: 'LR' | 'RL'): void {
    if (!currentTarget) return;
    const pose = getPose(currentTarget);
    for (const [l, r] of MIRROR_PAIRS) {
      const src = direction === 'LR' ? pose[l] : pose[r];
      const dstSlot = direction === 'LR' ? r : l;
      if (!src) { delete pose[dstSlot]; continue; }
      pose[dstSlot] = { x: src.x ?? 0, y: src.y != null ? -src.y : 0, z: src.z != null ? -src.z : 0 };
    }
    refreshSlidersFromPose();
    poseStatusEl.textContent = `Mirrored ${direction === 'LR' ? 'left → right' : 'right → left'}`;
  }

  mirrorLRBtn.addEventListener('click', () => mirror('LR'));
  mirrorRLBtn.addEventListener('click', () => mirror('RL'));
  zeroBtn.addEventListener('click', () => {
    if (!currentTarget) return;
    const pose = getPose(currentTarget);
    for (const slot of Object.keys(pose) as BoneSlot[]) delete pose[slot];
    refreshSlidersFromPose();
    poseStatusEl.textContent = 'All offsets zeroed';
  });
  resetBtn.addEventListener('click', () => {
    if (!currentTarget) return;
    const key = `${currentTarget.kind}:${currentTarget.name}`;
    const snap = snapshots.get(key);
    if (!snap) { poseStatusEl.textContent = 'No snapshot to reset to'; return; }
    const pose = getPose(currentTarget);
    for (const slot of Object.keys(pose) as BoneSlot[]) delete pose[slot];
    for (const slot of Object.keys(snap) as BoneSlot[]) { const o = snap[slot]; if (o) pose[slot] = { ...o }; }
    refreshSlidersFromPose();
    poseStatusEl.textContent = 'Reset to snapshot taken at panel open';
  });
  copyBtn.addEventListener('click', async () => {
    if (!currentTarget) return;
    const code = formatPoseAsCode(getPose(currentTarget));
    try {
      await navigator.clipboard.writeText(code);
      poseStatusEl.textContent = `Copied ${currentTarget.name} as TypeScript`;
    } catch (_) {
      console.log('[tuning] formatted pose:\n' + code);
      poseStatusEl.textContent = 'Clipboard unavailable — see console for code';
    }
  });

  // ══════════════════════════════════════════════════════════════════════════
  // METADATA EDIT — new pane
  // ══════════════════════════════════════════════════════════════════════════

  const metaClipSearch    = root.querySelector('.meta-clip-search') as HTMLInputElement;
  const metaClipCountEl   = root.querySelector('.meta-clip-count') as HTMLSpanElement;
  const metaClipList      = root.querySelector('.meta-clip-list') as HTMLSelectElement;
  const metaPreviewBtn    = root.querySelector('.meta-preview-btn') as HTMLButtonElement;
  const metaFieldsEl      = root.querySelector('.meta-fields') as HTMLDivElement;
  const metaStatusEl      = root.querySelector('.meta-status') as HTMLDivElement;
  const metaEmotionSel    = root.querySelector('.meta-emotion') as HTMLSelectElement;
  const metaIntensityIn   = root.querySelector('.meta-intensity') as HTMLInputElement;
  const metaIntensityVal  = root.querySelector('.meta-intensity-val') as HTMLSpanElement;
  const metaIsIdleCb      = root.querySelector('.meta-is-idle') as HTMLInputElement;
  const metaLoopableCb    = root.querySelector('.meta-loopable') as HTMLInputElement;
  const metaIsTransCb     = root.querySelector('.meta-is-transition') as HTMLInputElement;
  const metaSymmetricCb   = root.querySelector('.meta-symmetric') as HTMLInputElement;
  const metaTagsChips     = root.querySelector('.meta-tags-chips') as HTMLDivElement;
  const metaTagsInput     = root.querySelector('.meta-tags-input') as HTMLInputElement;
  const metaStartPoseSel  = root.querySelector('.meta-start-pose') as HTMLSelectElement;
  const metaEndPoseSel    = root.querySelector('.meta-end-pose') as HTMLSelectElement;
  const metaSaveBtn       = root.querySelector('.meta-save-btn') as HTMLButtonElement;

  // Populate emotion dropdown
  for (const e of EMOTION_VALUES) {
    const opt = document.createElement('option');
    opt.value = e;
    opt.textContent = e;
    metaEmotionSel.appendChild(opt);
  }

  // Populate start/end pose dropdowns
  for (const sel of [metaStartPoseSel, metaEndPoseSel]) {
    for (const p of POSE_VALUES) {
      const opt = document.createElement('option');
      opt.value = p;
      opt.textContent = p;
      sel.appendChild(opt);
    }
  }

  /** Current working copy of metadata for the selected clip. */
  let metaEntry: MetaEntry | null = null;
  let metaCurrentTags: string[] = [];

  // ── Metadata clip search ───────────────────────────────────────────────────
  function renderMetaClips(filter: string): void {
    const trimmed = filter.trim().toLowerCase();
    const tokens = trimmed.length > 0 ? trimmed.split(/\s+/) : [];
    const matches: string[] = [];
    for (const name of allClipNames) {
      if (tokens.length === 0 || tokens.every((t) => name.toLowerCase().includes(t))) matches.push(name);
    }
    metaClipList.innerHTML = '';
    for (const name of matches.slice(0, 2500)) {
      const opt = document.createElement('option');
      opt.value = name;
      opt.textContent = name;
      metaClipList.appendChild(opt);
    }
    metaClipCountEl.textContent = matches.length === allClipNames.length
      ? `${allClipNames.length}`
      : `${matches.length} / ${allClipNames.length}`;
  }

  metaClipSearch.addEventListener('input', () => renderMetaClips(metaClipSearch.value));

  metaClipList.addEventListener('change', () => {
    const name = metaClipList.value;
    if (!name) { metaPreviewBtn.disabled = true; return; }
    metaPreviewBtn.disabled = false;
    loadMetaEntry(name);
  });

  metaPreviewBtn.addEventListener('click', () => {
    const name = metaClipList.value;
    if (!name) return;
    const url = allClipsByName.get(name);
    if (!url) { metaStatusEl.textContent = `No URL for "${name}"`; return; }
    figure.playClipFromUrl(url, 0.25, true).catch((err) => {
      metaStatusEl.textContent = `Preview failed: ${err instanceof Error ? err.message : String(err)}`;
    });
    metaStatusEl.textContent = `Previewing "${name}"`;
  });

  // ── Load clip's existing metadata into fields ──────────────────────────────
  function loadMetaEntry(name: string): void {
    const raw = allClipsMeta.get(name) ?? {};
    metaEntry = {
      name,
      url: allClipsByName.get(name) ?? '',
      emotion:       typeof raw['emotion'] === 'string' ? raw['emotion'] : 'neutral',
      intensity:     typeof raw['intensity'] === 'number' ? raw['intensity'] : 0.5,
      is_idle:       Boolean(raw['is_idle']),
      is_transition: Boolean(raw['is_transition']),
      loopable:      Boolean(raw['loopable']),
      symmetric:     Boolean(raw['symmetric']),
      tags:          Array.isArray(raw['tags']) ? (raw['tags'] as string[]) : [],
      start_pose:    typeof raw['start_pose'] === 'string' ? raw['start_pose'] : 'standing_neutral',
      end_pose:      typeof raw['end_pose'] === 'string'   ? raw['end_pose']   : 'standing_neutral',
    };
    metaCurrentTags = [...metaEntry.tags];

    metaEmotionSel.value           = metaEntry.emotion;
    metaIntensityIn.value           = String(metaEntry.intensity);
    metaIntensityVal.textContent    = metaEntry.intensity.toFixed(2);
    metaIsIdleCb.checked            = metaEntry.is_idle;
    metaLoopableCb.checked          = metaEntry.loopable;
    metaIsTransCb.checked           = metaEntry.is_transition;
    metaSymmetricCb.checked         = metaEntry.symmetric;
    metaStartPoseSel.value          = metaEntry.start_pose;
    metaEndPoseSel.value            = metaEntry.end_pose;
    renderTagChips();
    metaFieldsEl.classList.remove('hidden');
    metaStatusEl.textContent = `Loaded: ${name}`;
  }

  // ── Tag chip editor ────────────────────────────────────────────────────────
  function renderTagChips(): void {
    metaTagsChips.innerHTML = '';
    for (const tag of metaCurrentTags) {
      const chip = document.createElement('span');
      chip.className = 'meta-tag-chip';
      chip.textContent = tag;
      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'meta-tag-remove';
      removeBtn.textContent = '×';
      removeBtn.setAttribute('aria-label', `Remove tag ${tag}`);
      removeBtn.addEventListener('click', () => {
        metaCurrentTags = metaCurrentTags.filter((t) => t !== tag);
        renderTagChips();
      });
      chip.appendChild(removeBtn);
      metaTagsChips.appendChild(chip);
    }
  }

  function addTag(raw: string): void {
    const tag = raw.trim().toLowerCase().replace(/,+$/, '');
    if (!tag) return;
    if (!metaCurrentTags.includes(tag)) {
      metaCurrentTags = [...metaCurrentTags, tag];
      renderTagChips();
    }
  }

  metaTagsInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(metaTagsInput.value);
      metaTagsInput.value = '';
    }
  });
  metaTagsInput.addEventListener('blur', () => {
    if (metaTagsInput.value.trim()) {
      addTag(metaTagsInput.value);
      metaTagsInput.value = '';
    }
  });

  // ── Live field sync ────────────────────────────────────────────────────────
  metaIntensityIn.addEventListener('input', () => {
    metaIntensityVal.textContent = parseFloat(metaIntensityIn.value).toFixed(2);
  });

  // ── Save to catalog ────────────────────────────────────────────────────────
  metaSaveBtn.addEventListener('click', async () => {
    if (!metaEntry) { metaStatusEl.textContent = 'Select a clip first.'; return; }
    if (!serverUrl) { metaStatusEl.textContent = 'No server URL — cannot save.'; return; }

    const patch = {
      emotion:       metaEmotionSel.value,
      intensity:     Math.round(parseFloat(metaIntensityIn.value) * 100) / 100,
      is_idle:       metaIsIdleCb.checked,
      is_transition: metaIsTransCb.checked,
      loopable:      metaLoopableCb.checked,
      symmetric:     metaSymmetricCb.checked,
      tags:          metaCurrentTags,
      start_pose:    metaStartPoseSel.value,
      end_pose:      metaEndPoseSel.value,
    };

    metaSaveBtn.disabled = true;
    metaStatusEl.textContent = `Saving "${metaEntry.name}"…`;
    try {
      const res = await fetch(
        `${serverUrl}/catalog/${encodeURIComponent(metaEntry.name)}`,
        { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) },
      );
      if (!res.ok) {
        const err = (await res.json().catch(() => ({ error: res.statusText }))) as { error?: string };
        metaStatusEl.textContent = `Save failed: ${err.error ?? res.statusText}`;
        return;
      }
      // Update the local cache so a re-select reflects the saved values.
      const saved = (await res.json()) as { clip: Record<string, unknown> };
      allClipsMeta.set(metaEntry.name, saved.clip);
      metaStatusEl.textContent = `✓ Saved "${metaEntry.name}"`;
    } catch (err) {
      metaStatusEl.textContent = `Save failed: ${err instanceof Error ? err.message : String(err)}`;
    } finally {
      metaSaveBtn.disabled = false;
    }
  });

  // ══════════════════════════════════════════════════════════════════════════
  // EMOTIONS PANE — preview emotion presets + one-shot gestures
  // ══════════════════════════════════════════════════════════════════════════

  const emoIntensitySlider = emoPane.querySelector('.emo-intensity-slider') as HTMLInputElement;
  const emoIntensityVal    = emoPane.querySelector('.emo-intensity-val') as HTMLSpanElement;
  const emoEmotionPills    = emoPane.querySelector('.emo-emotion-pills') as HTMLDivElement;
  const emoGesturePills    = emoPane.querySelector('.emo-gesture-pills') as HTMLDivElement;
  const emoClearBtn        = emoPane.querySelector('.emo-clear-btn') as HTMLButtonElement;
  const emoStatusEl        = emoPane.querySelector('.emo-status') as HTMLDivElement;

  let activeEmoPill: HTMLButtonElement | null = null;

  emoIntensitySlider.addEventListener('input', () => {
    emoIntensityVal.textContent = parseFloat(emoIntensitySlider.value).toFixed(2);
    if (activeEmoPill) activeEmoPill.click();
  });

  // Build emotion pills from the shared vocab + EMOTION_PRESETS
  for (const name of EMOTIONS) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'emo-pill';
    btn.textContent = name;
    btn.addEventListener('click', () => {
      const intensity = parseFloat(emoIntensitySlider.value);
      const base = EMOTION_PRESETS[name] ?? {};
      const scaled: Record<string, number> = {};
      for (const [shape, w] of Object.entries(base)) scaled[shape] = w * intensity;
      figure.setExpression(scaled);
      emoEmotionPills.querySelectorAll('.emo-pill').forEach(p => p.classList.remove('active'));
      btn.classList.add('active');
      activeEmoPill = btn;
      emoStatusEl.textContent = `emotion: ${name} @ intensity ${intensity.toFixed(2)}`;
    });
    emoEmotionPills.appendChild(btn);
  }

  // Build gesture pills from vocab
  for (const name of GESTURES) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'emo-pill emo-gesture-pill';
    btn.textContent = name.replace(/_/g, ' ');
    btn.addEventListener('click', () => {
      figure.triggerGesture(name);
      emoGesturePills.querySelectorAll('.emo-pill').forEach(p => p.classList.remove('active'));
      btn.classList.add('active');
      emoStatusEl.textContent = `gesture: ${name}`;
      // Auto-clear the active state after the longest gesture profile finishes
      setTimeout(() => btn.classList.remove('active'), 3000);
    });
    emoGesturePills.appendChild(btn);
  }

  emoClearBtn.addEventListener('click', () => {
    figure.setExpression({});
    emoEmotionPills.querySelectorAll('.emo-pill').forEach(p => p.classList.remove('active'));
    emoGesturePills.querySelectorAll('.emo-pill').forEach(p => p.classList.remove('active'));
    activeEmoPill = null;
    emoStatusEl.textContent = 'Cleared — expression reset to neutral';
  });

  // ══════════════════════════════════════════════════════════════════════════
  // WORLD PANE — splat transform controls with localStorage persistence
  // ══════════════════════════════════════════════════════════════════════════

  function loadStoredTransform(): SplatTransform {
    try {
      const stored = localStorage.getItem(SPLAT_TRANSFORM_LS_KEY);
      if (stored) return { ...SPLAT_TRANSFORM_DEFAULTS, ...(JSON.parse(stored) as Partial<SplatTransform>) };
    } catch { /* ignore */ }
    return { ...SPLAT_TRANSFORM_DEFAULTS };
  }

  let worldTransform: SplatTransform = loadStoredTransform();

  const worldScaleIn  = worldPane.querySelector('.world-scale') as HTMLInputElement;
  const worldPosXIn   = worldPane.querySelector('.world-pos-x') as HTMLInputElement;
  const worldPosYIn   = worldPane.querySelector('.world-pos-y') as HTMLInputElement;
  const worldPosZIn   = worldPane.querySelector('.world-pos-z') as HTMLInputElement;
  const worldRotYIn   = worldPane.querySelector('.world-rot-y') as HTMLInputElement;
  const worldClipWIn  = worldPane.querySelector('.world-clip-w') as HTMLInputElement;
  const worldClipDIn  = worldPane.querySelector('.world-clip-d') as HTMLInputElement;
  const worldScaleVal = worldPane.querySelector('.world-scale-val') as HTMLSpanElement;
  const worldPosXVal  = worldPane.querySelector('.world-pos-x-val') as HTMLSpanElement;
  const worldPosYVal  = worldPane.querySelector('.world-pos-y-val') as HTMLSpanElement;
  const worldPosZVal  = worldPane.querySelector('.world-pos-z-val') as HTMLSpanElement;
  const worldRotYVal  = worldPane.querySelector('.world-rot-y-val') as HTMLSpanElement;
  const worldClipWVal = worldPane.querySelector('.world-clip-w-val') as HTMLSpanElement;
  const worldClipDVal = worldPane.querySelector('.world-clip-d-val') as HTMLSpanElement;
  const worldResetBtn = worldPane.querySelector('.world-reset-btn') as HTMLButtonElement;
  const worldStatusEl = worldPane.querySelector('.world-status') as HTMLDivElement;

  function syncWorldSliders(t: SplatTransform): void {
    worldScaleIn.value  = String(t.scale);
    worldPosXIn.value   = String(t.posX);
    worldPosYIn.value   = String(t.posY);
    worldPosZIn.value   = String(t.posZ);
    worldRotYIn.value   = String(t.rotY);
    worldClipWIn.value  = String(t.clipW);
    worldClipDIn.value  = String(t.clipD);
    worldScaleVal.textContent = t.scale.toFixed(1);
    worldPosXVal.textContent  = t.posX.toFixed(1);
    worldPosYVal.textContent  = t.posY.toFixed(1);
    worldPosZVal.textContent  = t.posZ.toFixed(1);
    worldRotYVal.textContent  = `${t.rotY.toFixed(0)}°`;
    worldClipWVal.textContent = t.clipW > 0 ? t.clipW.toFixed(1) : 'off';
    worldClipDVal.textContent = t.clipD > 0 ? t.clipD.toFixed(1) : 'off';
  }

  // Initialise sliders from stored / default values
  syncWorldSliders(worldTransform);

  function onWorldSliderChange(): void {
    worldTransform = {
      scale: parseFloat(worldScaleIn.value),
      posX:  parseFloat(worldPosXIn.value),
      posY:  parseFloat(worldPosYIn.value),
      posZ:  parseFloat(worldPosZIn.value),
      rotY:  parseFloat(worldRotYIn.value),
      clipW: parseFloat(worldClipWIn.value),
      clipD: parseFloat(worldClipDIn.value),
    };
    worldScaleVal.textContent = worldTransform.scale.toFixed(1);
    worldPosXVal.textContent  = worldTransform.posX.toFixed(1);
    worldPosYVal.textContent  = worldTransform.posY.toFixed(1);
    worldPosZVal.textContent  = worldTransform.posZ.toFixed(1);
    worldRotYVal.textContent  = `${worldTransform.rotY.toFixed(0)}°`;
    worldClipWVal.textContent = worldTransform.clipW > 0 ? worldTransform.clipW.toFixed(1) : 'off';
    worldClipDVal.textContent = worldTransform.clipD > 0 ? worldTransform.clipD.toFixed(1) : 'off';
    try { localStorage.setItem(SPLAT_TRANSFORM_LS_KEY, JSON.stringify(worldTransform)); } catch { /* ignore */ }
    opts.onSplatTransformChange?.(worldTransform);
  }

  for (const el of [worldScaleIn, worldPosXIn, worldPosYIn, worldPosZIn, worldRotYIn, worldClipWIn, worldClipDIn]) {
    el.addEventListener('input', onWorldSliderChange);
  }

  worldResetBtn.addEventListener('click', () => {
    worldTransform = { ...SPLAT_TRANSFORM_DEFAULTS };
    syncWorldSliders(worldTransform);
    try { localStorage.setItem(SPLAT_TRANSFORM_LS_KEY, JSON.stringify(worldTransform)); } catch { /* ignore */ }
    opts.onSplatTransformChange?.(worldTransform);
    worldStatusEl.textContent = 'Reset to defaults';
  });

  // ══════════════════════════════════════════════════════════════════════════
  // Shared close button + open/close lifecycle
  // ══════════════════════════════════════════════════════════════════════════

  closeBtn.addEventListener('click', () => close());

  function open(initialTab: 'pose' | 'meta' | 'emotions' | 'world' = 'pose'): void {
    if (isOpen) return;
    isOpen = true;
    beatPlayer.stop();
    // Snapshot every tunable pose for "Reset to original".
    snapshots.clear();
    for (const name of ALL_PROCEDURAL_HOLDS) snapshots.set(`hold:${name}`, deepClonePose(PROCEDURAL_HOLD_POSES[name]));
    for (const name of ALL_BODY_STATES) snapshots.set(`body_state:${name}`, deepClonePose(BODY_STATE_PROFILES[name].bias));
    root.classList.remove('hidden');
    showTab(initialTab);
    if (initialTab === 'pose') {      targetSelect.value = `hold:${ALL_PROCEDURAL_HOLDS[0]}`;
      applyTarget({ kind: 'hold', name: ALL_PROCEDURAL_HOLDS[0] });
      void ensureAllClipsLoaded().then(() => renderAllClips(allClipsSearch.value));
      const debugPoseScript = typeof window !== 'undefined'
        && new URLSearchParams(window.location.search).has('debug-posescript');
      if (serverUrl && debugPoseScript) {
        setPoseSectionVisible(true);
        void fetchPoseStats();
        void runPoseSearch(poseSearchInput.value);
      } else {
        setPoseSectionVisible(false);
      }
    }
  }

  function close(): void {
    if (!isOpen) return;
    isOpen = false;
    figure.releaseAllHolds(0.3);
    figure.setBodyState('neutral_idle');
    figure.setExpression({});
    if (clipMode === 'preview') revertToIdle();
    clipMode = 'idle';
    activeClipName = null;
    allClipsSelect.value = '';
    clipSelect.value = '';
    metaClipList.value = '';
    metaFieldsEl.classList.add('hidden');
    metaEntry = null;
    root.classList.add('hidden');
    currentTarget = null;
    activeEmoPill = null;
  }

  function toggle(): void { if (isOpen) close(); else open(); }

  function destroy(): void {
    close();
    root.remove();
  }

  return {
    open,
    close,
    toggle,
    destroy,
    get isOpen(): boolean { return isOpen; },
  };
}
